// 로그 한 줄의 심각도를 토큰에서 읽어낸다. 모든 로그 소비자가 같은 규칙을 쓴다.
type LogLevel = "debug" | "info" | "warn" | "error" | "critical";

// 색을 입히는 레벨. INFO 는 콘솔 기본 텍스트색이므로 대상이 아니다. 대부분의 줄이 INFO 인
// 로그에서 색이 뜻을 잃지 않게 한다.
type LogHighlight = Exclude<LogLevel, "info">;

// 색이 같은 연속 구간. 렌더 측은 이 단위로만 엘리먼트를 만든다.
type LogRun = { highlight: LogHighlight | null; text: string };

const LEVEL_WORDS = "debug|info|warn(?:ing)?|error|critical|fatal";

// 레벨 토큰 형태 4종. 대괄호 형태를 뺀 나머지는 위치를 고정한다. 고정하지 않으면 본문의 단어
// ("raised an error")가 줄 전체를 빨갛게 만든다. 교대 정규식이므로 가장 앞의 매치를 쓴다.
const LEVEL_RE = new RegExp(
  [
    // [INFO]: … · [warning] …. 상류 learning-core·Isaac 로그.
    String.raw`\[(${LEVEL_WORDS})\]`,
    // ERROR:root:… · INFO:     …. python logging 기본 포맷.
    String.raw`^[ \t]*(${LEVEL_WORDS})[ \t]*:`,
    // … - WARNING - …. 구분자로 둘러싸인 필드.
    String.raw`[ \t]-[ \t](${LEVEL_WORDS})[ \t]-[ \t]`,
    // 2026-08-11 12:00:00,123 INFO …. 숫자와 구분자만 있는 타임스탬프 뒤의 필드.
    String.raw`^[ \t]*\d[\d\-/:.,T ]{5,30}[ \t](${LEVEL_WORDS})\b`,
  ].join("|"),
  "i",
);

function normalizeLevel(word: string): LogLevel {
  const lower = word.toLowerCase();
  if (lower === "warning") {
    return "warn";
  }
  if (lower === "fatal") {
    return "critical";
  }
  return lower as LogLevel;
}

/**
 * 로그 한 줄의 레벨을 읽는다.
 *
 * Args:
 *   line: 로그 한 줄. 개행을 포함하지 않는다.
 *
 * Returns:
 *   정규화한 레벨. 어떤 형태로도 레벨 토큰이 없으면 `null`.
 */
export function detectLogLevel(line: string): LogLevel | null {
  const match = LEVEL_RE.exec(line);
  if (match === null) {
    return null;
  }
  const word = match[1] ?? match[2] ?? match[3] ?? match[4];
  return word === undefined ? null : normalizeLevel(word);
}

/**
 * 색이 같은 연속 줄을 한 구간(run)으로 묶는다.
 *
 * 5000줄 버퍼를 3초마다 다시 렌더한다. 줄마다 엘리먼트를 만들면 tick 마다 5000개를 재조정한다.
 * 같은 색이 길게 이어지는 실제 로그에서는 이 묶음이 엘리먼트 수를 수십 개로 줄인다.
 * INFO 와 매치되지 않은 줄은 같은 기본색이므로 한 구간으로 합친다.
 *
 * Args:
 *   lines: 로그 줄 버퍼.
 *
 * Returns:
 *   입력 순서를 보존한 구간 목록. 구간 안의 줄은 개행으로 이어 붙인다.
 */
export function groupLogLines(lines: readonly string[]): LogRun[] {
  const runs: LogRun[] = [];
  for (const line of lines) {
    const level = detectLogLevel(line);
    const highlight = level === null || level === "info" ? null : level;
    const last = runs.at(-1);
    if (last !== undefined && last.highlight === highlight) {
      last.text = `${last.text}\n${line}`;
      continue;
    }
    runs.push({ highlight, text: line });
  }
  return runs;
}
